import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Asset } from 'contentful';
import Link from 'next/link';
import { ProjectFilter } from '../../../../types/ProjectTypes';
import { colors, spacing } from '../../../../utils/styleguide';
import DeprecatedImage from '../../../contentful-elements/Image';
import PrimaryButton from '../../buttons/PrimaryButton';
import Icon from '../../Icon';
import Image from '../../Image';
import Typography from '../../text/Typography';

const InitiativeWrapper = styled.div<{
  border?: boolean;
  backgroundColor?: string;
  href?: string;
}>`
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 420px;
  width: 100%;
  background-color: ${(props: { backgroundColor?: string }) =>
    props.backgroundColor === colors.white ? colors.grey01 : colors.white};

  ${({ border }) => {
    if (border) {
      return `
        border: 1px solid ${colors.grey02};
        border-radius: 4px;
      `;
    }
  }}
  :hover {
    background-color: ${({ href }) => (href ? colors.grey01 : 'none')};
  }
`;

const ImageWrapper = styled.div`
  position: relative;
`;

const GradientOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 99%;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5));
`;

const PreviouslySupported = styled(Typography)`
  padding-top: ${spacing[5]}px;
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background-color: ${(props) => props.color || colors.orange100};
  padding: ${spacing[1]}px ${spacing[3]}px;
`;

const ContentWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${spacing[3]}px;
`;

const LocationWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: ${spacing[3]}px 0;
`;

const Location = styled(Typography)`
  margin-left: ${spacing[1]}px;
`;

const Button = styled(PrimaryButton)`
  margin-top: ${spacing[4]}px;
  width: 140px;
  border: none;
`;

const StyledImage = styled(DeprecatedImage)<{ hasOverlay?: boolean }>`
  height: 220px;
  width: 100%;
  object-fit: cover;
  filter: ${({ hasOverlay }) => (hasOverlay ? 'brightness(0.6)' : 'none')};
`;

const ImageOverlayIconWrapper = styled.div`
  position: absolute;
  right: 0;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  text-align: center;
`;

const ImageOverlayIconCss = css`
  max-width: 50%;
  min-width: 10%;
  max-height: 120px;
  filter: brightness(0) saturate(100%) invert(100%) sepia(10%) saturate(7437%) hue-rotate(290deg)
    brightness(119%) contrast(100%);
`;

type Props = {
  image?: Asset<'WITHOUT_UNRESOLVABLE_LINKS'>;
  title?: string;
  location?: string;
  description?: string;
  className?: string;
  filter?: Pick<ProjectFilter, 'filterName' | 'backgroundColor'>;
  previouslySupported?: boolean;
  learnMoreUrl?: string;
  backgroundColor?: string;
  border?: boolean;
  overlayIconUrl?: string;
  overlayIconAlt?: string;
  cardLinkUrl?: string;
};

const InitiativeCard = ({
  image,
  title,
  location,
  description,
  className,
  filter,
  previouslySupported,
  learnMoreUrl,
  backgroundColor,
  border,
  overlayIconUrl,
  overlayIconAlt,
  cardLinkUrl,
}: Props) => {
  const textColor = previouslySupported ? colors.blackDisabled : colors.black;
  const card = (
    <InitiativeWrapper
      href={cardLinkUrl}
      className={className}
      backgroundColor={backgroundColor}
      border={border}
    >
      {image && (
        <ImageWrapper>
          <StyledImage
            asset={image}
            maxWidth={{ desktop: 600, tablet: 500 }}
            hasOverlay={!!overlayIconUrl}
          />
          {filter?.filterName && (
            <Overlay color={filter?.backgroundColor}>
              <Typography color={colors.black} variant="body">
                {filter?.filterName}
              </Typography>
            </Overlay>
          )}
          {previouslySupported && (
            <>
              <GradientOverlay />
              <PreviouslySupported color={colors.white} variant="bodySmall">
                PREVIOUSLY SUPPORTED
              </PreviouslySupported>
            </>
          )}
          {!previouslySupported && overlayIconUrl && (
            <ImageOverlayIconWrapper>
              <Image
                url={overlayIconUrl}
                maxWidth={300}
                alt={overlayIconAlt ?? ''}
                css={ImageOverlayIconCss}
              />
            </ImageOverlayIconWrapper>
          )}
        </ImageWrapper>
      )}
      <ContentWrapper>
        <div>
          <Typography color={textColor} variant="overline">
            {title}
          </Typography>
          {location && (
            <LocationWrapper>
              <Icon name="map-marker-filled" size={spacing[3]} />
              <Location color={textColor} variant="body">
                {location}
              </Location>
            </LocationWrapper>
          )}
          <Typography maxNumberOfLines={3} color={colors.grey04} variant="bodySmall">
            {description}
          </Typography>
        </div>
        {learnMoreUrl && (
          <Button href={learnMoreUrl} size="small">
            Learn more
          </Button>
        )}
      </ContentWrapper>
    </InitiativeWrapper>
  );

  if (cardLinkUrl) {
    return (
      <Link
        href={cardLinkUrl as string}
        passHref
        css={css`
          text-decoration: none;
        `}
      >
        {card}
      </Link>
    );
  }

  return card;
};

export default InitiativeCard;
